<template>
  <div class="mx-sm-1">
    <div class="page-header">
      <h1 class="page-title fw-bold">Catégories</h1>
    </div>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="card-title fw-bold mb-0">Liste des Catégories</h3>

        <div class="d-flex align-items-center gap-2">
          <!-- Switch Vue -->
          <div class="btn-group me-2" role="group">
            <button class="btn btn-sm"
                    :class="view === 'table' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="view = 'table'">
              Table
            </button>
            <button class="btn btn-sm"
                    :class="view === 'accordion' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="view = 'accordion'">
              Arborescence
            </button>
          </div>

          <!-- Boutons existants -->
          <div class="card-options d-flex gap-2">
            <button class="btn btn-outline-success btn-sm" @click="showCreateCategoryModal">
              <i class="fe fe-plus-circle me-1"></i> Ajouter
            </button>
            <button class="btn btn-outline-info btn-sm" @click="exportCSV">
              <i class="fe fe-download me-1"></i> Exporter
            </button>
            <button class="btn btn-outline-primary btn-sm" @click="triggerFileInput">
              <i class="fe fe-upload me-1"></i> Importer
            </button>
            <input type="file" ref="fileInput" class="d-none" accept=".xlsx, .xls" @change="importCSV" />
          </div>
        </div>
      </div>


      <div class="card-body" v-if="view === 'table'">
        <TableWithFilters
            :columns="columns"
            :filterableFields="filterableFields"
            :row-actions="rowActions"
            :items="categories"
            @edit="onEdit"
            @delete="onDelete"
        >
          <template #cell-code="{ item }">
            <strong>{{ item.code }}</strong>
          </template>
          <template #cell-label="{ item }">
            {{ item.label }}
          </template>
        </TableWithFilters>
      </div>

      <div class="card-body p-0" v-if="view === 'accordion'">
        <table class="table table-hover table-bordered text-nowrap mb-0">
          <thead class="bg-light">
          <tr>
            <th style="width:40px"></th>
            <th class="fw-bold">Code</th>
            <th class="fw-bold">Nom de la catégorie</th>
            <th class="fw-bold" style="width:120px">Descendants</th>
            <th class="fw-bold" style="width:120px">Actions</th>
          </tr>
          </thead>

          <!-- Groupe par racine -->
          <tbody v-for="root in treeRoots" :key="root.id">
          <!-- Ligne parent -->
          <tr class="bg-secondary-transparent cursor-pointer"
              @click="toggleNode(root.id)">
            <td class="ps-2">
              <i :class="isOpen(root.id) ? 'fe fe-minus' : 'fe fe-plus'"></i>
            </td>
            <td class="fw-bold">{{ root.code }}</td>
            <td class="fw-bold">{{ root.label }}</td>
            <td>
              <span class="badge bg-secondary">{{ countDescendants(root) }}</span>
            </td>
            <td class="text-end">
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-primary" @click.stop="onEdit(root)"><i class="fe fe-edit"></i></button>
                <button class="btn btn-outline-danger" @click.stop="onDelete(root)"><i class="fe fe-trash"></i></button>
              </div>
            </td>
          </tr>

          <!-- Enfants (niveaux récursifs aplatis) -->
          <template v-if="isOpen(root.id)">
            <tr v-for="row in flattenedChildren(root)" :key="row.node.id">
              <td></td>
              <td>
            <span :style="{ paddingLeft: (row.level * 20) + 'px' }" class="d-inline-flex align-items-center">
              <i v-if="row.node.children.length"
                 class="me-1 fe"
                 :class="isOpen(row.node.id) ? 'fe fe-minus' : 'fe fe-plus'"
                 @click.stop="toggleNode(row.node.id)"></i>
              <strong>{{ row.node.code }}</strong>
            </span>
              </td>
              <td>{{ row.node.label }}</td>
              <td>
                <span class="badge bg-light text-dark">{{ countDescendants(row.node) }}</span>
              </td>
              <td class="text-end">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-primary" @click.stop="onEdit(row.node)"><i class="fe fe-edit"></i></button>
                  <button class="btn btn-outline-danger" @click.stop="onDelete(row.node)"><i class="fe fe-trash"></i></button>
                </div>
              </td>
            </tr>
          </template>
          </tbody>

          <tbody v-if="!treeRoots.length">
          <tr><td colspan="5" class="text-center text-muted">Aucune catégorie</td></tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- Modal de création -->
    <modal-create-category
        :category="selectedCategory"
        @refresh="loadCategories"
    />

    <div class="modal fade"
         id="modal-import-preview"
         tabindex="-1"
         role="dialog"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header bg-secondary text-white">
            <h5 class="modal-title">Prévisualisation de l'import</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"
                    @click="resetCsvPreview"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3 d-flex flex-wrap gap-2">
              <span class="badge bg-secondary">Total : {{ csvPreview.total }}</span>
              <span class="badge bg-success">À créer : {{ csvPreview.toCreate.length }}</span>
              <span class="badge bg-info">Existants (ignorés) : {{ csvPreview.existingDb.length }}</span>
              <span class="badge bg-warning text-dark">Doublons fichier : {{ csvPreview.duplicatesFile.length }}</span>
              <span class="badge bg-danger">Invalides : {{ csvPreview.invalid.length }}</span>
            </div>

            <div v-if="csvPreview.toCreate.length">
              <h6 class="fw-bold mb-2">Lignes à créer</h6>
              <div class="table-responsive mb-4">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light">
                  <tr>
                    <th>code</th>
                    <th>label</th>
                    <th>Code Parent</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.toCreate" :key="'create-'+i">
                    <td>{{ r.code }}</td>
                    <td>{{ r.label }}</td>
                    <td>{{ r.parent }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <details v-if="csvPreview.existingDb.length" class="mb-3">
              <summary class="fw-bold">Déjà existants (ignorés)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.existingDb" :key="'exist-'+i">
                    <td>{{ r.code }}</td><td>{{ r.label }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="csvPreview.duplicatesFile.length" class="mb-3">
              <summary class="fw-bold">Doublons dans le fichier</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.duplicatesFile" :key="'dup-'+i">
                    <td>{{ r.code }}</td><td>{{ r.label }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="csvPreview.invalid.length" class="mb-3">
              <summary class="fw-bold">Invalides (champ manquant)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.invalid" :key="'inv-'+i">
                    <td>{{ r.code }}</td><td>{{ r.label }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-gray" data-bs-dismiss="modal" @click="resetCsvPreview">
              Fermer
            </button>
            <button type="button" class="btn btn-success"
                    :disabled="!csvPreview.toCreate.length || savingImport"
                    @click="saveImportedCategories">
              <i class="fe fe-check me-1"></i>
              Enregistrer ({{ csvPreview.toCreate.length }})
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ModalCreateCategory from "../../../components/modals/ModalCreateCategory.vue";
  import TableWithFilters from "../../../components/TableWithFilters.vue";
  import {categoryColumns, categoryFilterableFields, categoryRowActions} from "../../../helpers/constants";
  import {confirmAction} from "../../../helpers/helpers";
  import {API} from "../../../helpers/api-endpoints";
  import * as XLSX from "xlsx";

  export default {
    name: "Categories",
    components: {
      TableWithFilters,
      ModalCreateCategory,
    },
    data() {
      return {
        view: 'table',
        nodesOpen: {},
        categories: [],
        selectedCategory: null,
        columns: categoryColumns,
        filterableFields: categoryFilterableFields,
        rowActions: categoryRowActions,

        csvPreview: {
          total: 0,
          toCreate: [],
          existingDb: [],
          duplicatesFile: [],
          invalid: [],
          headers: [],
        },
        savingImport: false,

      };
    },
    computed: {
      // Construit l'arbre depuis la liste plate
      treeRoots() {
        const items = (this.categories || []).map(c => ({
          ...c,
          children: [],           // on injecte un tableau enfants
        }));

        const byId = new Map(items.map(c => [c.id, c]));
        const roots = [];

        for (const c of items) {
          if (c.parentId && byId.has(c.parentId)) {
            byId.get(c.parentId).children.push(c);
          } else {
            roots.push(c);
          }
        }

        // tri (optionnel) par label puis code
        const sortFn = (a, b) => (a.label || '').localeCompare(b.label || '') || (a.code || '').localeCompare(b.code || '');
        const sortDeep = (node) => {
          node.children.sort(sortFn).forEach(sortDeep);
        };
        roots.sort(sortFn).forEach(sortDeep);

        return roots;
      },
    },
    methods: {
      isOpen(id) {
        return !!this.nodesOpen[id];
      },
      toggleNode(id) {
        this.$set(this.nodesOpen, id, !this.nodesOpen[id]);
      },
      // Parcours récursif “aplati” pour l’affichage (respecte l’ouverture des nœuds)
      flattenedChildren(root) {
        const out = [];
        const walk = (node, level) => {
          for (const child of node.children) {
            out.push({ node: child, level });
            if (this.isOpen(child.id) && child.children?.length) {
              walk(child, level + 1);
            }
          }
        };
        walk(root, 1);
        return out;
      },
      // Compter tous les descendants
      countDescendants(node) {
        let n = 0;
        const dfs = (x) => {
          for (const ch of x.children || []) {
            n++;
            dfs(ch);
          }
        };
        dfs(node);
        return n;
      },
      triggerFileInput() { this.$refs.fileInput.click(); },

      async importCSV(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            this.buildCsvPreview(e.target.result);  // <<< construit les listes

          } catch (err) {
            console.error("Erreur import CSV :", err);
            this.$toast.error("Erreur lors de la lecture du CSV.");
          } finally {
            event.target.value = ""; // reset input
          }
        };
        reader.readAsArrayBuffer(file);
      },

      buildCsvPreview(text) {
        const data = new Uint8Array(text);
        const workbook = XLSX.read(data, { type: "array" });

        // On cherche la feuille nommée "Famille"
        const targetSheetName = workbook.SheetNames.find(name => name.toLowerCase() === "Categories".toLowerCase());

        if (!targetSheetName) {
          console.error("❌ La feuille 'Categorie' est introuvable dans le fichier Excel.");
          this.$toast.error("❌ La feuille 'Categorie' est introuvable dans le fichier Excel.");
          this.resetCsvPreview();
          return;
        }

        const worksheet = workbook.Sheets[targetSheetName];
        // Convertir en JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!jsonData.length) {
          this.$toast.warning("Fichier XLSX vide.");
          this.resetCsvPreview();
          return;
        }

        const headers = jsonData[0];

        // Vérification des colonnes
        const codeIdx = headers.indexOf("code");
        const labelIdx = headers.indexOf("label");
        const parentIdx = headers.indexOf("parent");

        if (codeIdx === -1 || labelIdx === -1) {
          this.$toast.error("Le XLSX doit contenir les colonnes 'code' et 'label'.");
          this.resetCsvPreview();
          return;
        }

        const rows = [];
        for (let i = 1; i < jsonData.length; i++) {
          const line = jsonData[i];
          const row = {
            code: (line[codeIdx] ?? "").toString().trim(),
            label: (line[labelIdx] ?? "").toString().trim(),
          };
          // Ajout de parent si présent
          if (parentIdx !== -1) {
            const parentValue = (line[parentIdx] ?? "").toString().trim();
            if (parentValue) row.parent = parentValue;
          }
          rows.push(row);
        }

// Nettoyage / stats
        const cleaned = rows.filter(r => r.code || r.label);
        const invalid = cleaned.filter(r => !r.code || !r.label);

// Doublons fichier (par code ou code+parent si parent existant)
        const seen = new Set();
        const duplicatesFile = [];
        const uniqueByFile = [];

        for (const r of cleaned) {
          if (!r.code || !r.label) continue; // laisser aux invalides

          // clé unique : code + parent (parent vide si non défini)
          const key = (r.code || "").toLowerCase() + "|" + ((r.parent || "").toLowerCase());

          if (seen.has(key)) duplicatesFile.push(r);
          else {
            seen.add(key);
            uniqueByFile.push(r);
          }
        }

// Déjà en base (si catégories en base ont aussi parent)
        const existingKeys = new Set(
            this.categories.map(c => (c.code || "").toLowerCase() + "|" + ((c.parent || "").toLowerCase()))
        );

        const existingDb = uniqueByFile.filter(r => existingKeys.has((r.code || "").toLowerCase() + "|" + ((r.parent || "").toLowerCase())));
        const toCreate = uniqueByFile.filter(r => !existingKeys.has((r.code || "").toLowerCase() + "|" + ((r.parent || "").toLowerCase())));

        this.csvPreview = {
          total: rows.length,
          toCreate,
          existingDb,
          duplicatesFile,
          invalid,
          headers,
        };
        $('#modal-import-preview').modal('show'); // <<< ouvre la modale
      },

      splitCsvLine(line) {
        const out = []; let cur = ""; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
            else inQ = !inQ;
          } else if (ch === ',' && !inQ) {
            out.push(cur); cur = "";
          } else cur += ch;
        }
        out.push(cur);
        return out;
      },

      async saveImportedCategories() {
        if (!this.csvPreview.toCreate.length) return;

        const { confirmed } = await confirmAction("validate");
        if (!confirmed) return;

        this.savingImport = true;
        this.$loading(true);

        try {
          // Version séquentielle (simple). Si tu as un endpoint batch, remplace par un POST /import.
          let ok = 0, ko = 0;
          for (const c of this.csvPreview.toCreate) {
            try {
              await this.$http.post(API.REGISTRY_CATEGORIES, { code: c.code, label: c.label, parentCode: c.parent });
              ok++;
            } catch (e) {
              console.error("Échec création :", c, e);
              ko++;
            }
          }

          this.$toast.success(`Import terminé. Créées: ${ok}, Échouées: ${ko}`);
          $('#modal-import-preview').modal('hide');
          this.resetCsvPreview();
          await this.loadCategories();
        } catch (err) {
          console.error(err);
          this.$toast.error("Erreur lors de l'enregistrement des catégories.");
        } finally {
          this.$loading(false);
          this.savingImport = false;
        }
      },

      // <<< Reset preview
      resetCsvPreview() {
        this.csvPreview = {
          total: 0, toCreate: [], existingDb: [], duplicatesFile: [], invalid: [], headers: [],
        };
      },
      showCreateCategoryModal() {
        this.selectedCategory = null;
        $('#modal-create-category').modal('show');
      },
      loadCategories() {
        this.$loading(true);
        this.$http
            .get(API.REGISTRY_CATEGORIES)
            .then(({ data }) => {
              this.categories = data;

              // Ouvrir par défaut les racines à la première charge
              const rootIds = (this.categories || [])
                  .filter(c => !c.parentId)
                  .map(c => c.id);
              rootIds.forEach(id => { if (this.nodesOpen[id] == null) this.$set(this.nodesOpen, id, true); });

            })
            .catch((err) => {
              console.error("Erreur lors du chargement des catégories :", err);
            })
            .finally(() => this.$loading(false));
      },

      onEdit(item) {
        this.selectedCategory = { ...item };
        $('#modal-create-category').modal('show');
      },
      async onDelete(item) {
        try {
          const { confirmed } = await confirmAction("delete");
          if (!confirmed) return;

          this.$loading(true);
          await this.$http.delete(API.REGISTRY_CATEGORY_DELETE(item.id)); //
          this.$toast.success("Catégorie supprimée avec succès !");
          await this.loadCategories();
        } catch (err) {
          console.error("Erreur suppression catégorie :", err);
          this.$toast.error("Impossible de supprimer la catégorie.");
        } finally {
          this.$loading(false);
        }
      },
      exportCSV() {
        const headers = this.columns.map(col => col.label);
        const rows = this.categories.map(row =>
            this.columns.map(col => `"${row[col.key] ?? ''}"`).join(",")
        );
        const csvContent = [headers.join(","), ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "categories.csv");
        link.click();
        URL.revokeObjectURL(url);
      },
    },
    created() {
      this.loadCategories();
    },
  };
</script>
